import { Card, Grow, Skeleton } from "@mui/material";
import { ReactNode } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";

import AnimatedNumber from "../AnimatedNumber";
import { DashboardColor, pulseSx, staggerDelay, surfaceCardSx } from "../styles";

type Props = {
  title: string;
  value: number;
  formatter: (value: number) => string;
  icon: ReactNode;
  iconColor: DashboardColor;
  index?: number;
  compact?: boolean;
  isLoading?: boolean;
  isRefreshing?: boolean;
  /** Replaces the animated value, e.g. when there is no data to show. */
  displayValue?: string;
  footer?: ReactNode;
  errorInfo?: {
    shouldLoadError: boolean;
    title: string;
    description: string;
    alt: string;
  };
};

const KpiCard = ({
  title,
  value,
  formatter,
  icon,
  iconColor,
  index = 0,
  compact = false,
  isLoading = false,
  isRefreshing = false,
  displayValue,
  footer,
  errorInfo = { shouldLoadError: false, title: "", description: "", alt: "" },
}: Props) => {
  return (
    <Card sx={{ ...surfaceCardSx(iconColor), height: "100%" }}>
      <MDBox
        p={compact ? 2.25 : 2.5}
        height="100%"
        minHeight={compact ? "5.75rem" : undefined}
        display="flex"
        alignItems="center"
        sx={{ "& > *": { width: "100%" } }}
      >
        {errorInfo.shouldLoadError ? (
          <AlertBlock
            title={errorInfo.title}
            description={errorInfo.description}
            alt={errorInfo.alt}
          />
        ) : isLoading ? (
          <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
            <MDBox flex={1} mr={1}>
              <Skeleton variant="text" width="70%" height={18} />
              <Skeleton variant="text" width="50%" height={40} />
            </MDBox>
            <Skeleton variant="rounded" width={48} height={48} />
          </MDBox>
        ) : (
          <Grow in timeout={500} style={{ transitionDelay: staggerDelay(index) }}>
            <MDBox sx={pulseSx(isRefreshing)}>
              <MDBox
                display="flex"
                flexDirection={compact ? "row" : "row"}
                justifyContent={compact ? "flex-start" : "space-between"}
                alignItems={compact ? "center" : "flex-start"}
                gap={compact ? 1.25 : 0}
              >
                {compact && (
                  <MDBox
                    variant="gradient"
                    bgColor={iconColor}
                    color="white"
                    width="2rem"
                    height="2rem"
                    borderRadius="lg"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    shadow="sm"
                    sx={{ flexShrink: 0 }}
                  >
                    {icon}
                  </MDBox>
                )}
                <MDBox lineHeight={1.15} pr={1} sx={{ minWidth: 0 }}>
                  <MDTypography
                    variant="caption"
                    fontWeight="medium"
                    color="text"
                    sx={compact ? { fontSize: "0.7rem", display: "block" } : undefined}
                  >
                    {title}
                  </MDTypography>
                  <MDTypography
                    variant={compact ? "h5" : "h3"}
                    fontWeight="bold"
                    mt={compact ? 0.1 : 0.5}
                  >
                    {displayValue ?? <AnimatedNumber value={value} formatter={formatter} />}
                  </MDTypography>
                </MDBox>
                {!compact && (
                  <MDBox
                    variant="gradient"
                    bgColor={iconColor}
                    color="white"
                    width="2.5rem"
                    height="2.5rem"
                    borderRadius="lg"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    shadow="md"
                    sx={{ flexShrink: 0 }}
                  >
                    {icon}
                  </MDBox>
                )}
              </MDBox>
              {footer && <MDBox mt={1}>{footer}</MDBox>}
            </MDBox>
          </Grow>
        )}
      </MDBox>
    </Card>
  );
};

export default KpiCard;
